.home-page {
  // 设计稿内容区约 1200–1400px；全宽模块（Hero / USP / 产品栅格）单独拉满视口
  --content-max: 1500px;
  --container-pad: clamp(16px, 4vw, 48px);
  --tap-min: 44px;

  // Some pages ship global `.page { height: ...; overflow: hidden; }` rules (from auto-generated CSS).
  // Home relies on natural document flow; ensure the wrapper doesn't clip sections like `.product-grid`.
  .page {
    width: 100%;
    min-height: 100%;
    height: auto;
    overflow: visible;
    position: relative;
    background-color: rgba(255, 255, 255, 1);
  }

  .layout-container {
    width: 100%;
    max-width: var(--content-max);
    margin-inline: auto;
    padding-inline: var(--container-pad);
    box-sizing: border-box;
  }

  // ---------------------------------------------------------------------------
  // Hero
  // ---------------------------------------------------------------------------
  .hero-section {
    position: relative;
    width: 100%;
    min-height: clamp(620px, 78vh, 920px);
    background: url(../../assets/images/pages-home-hero-section.png) no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
  }

  .hero-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
  }

  .hero-overlay {
    background-color: rgba(22, 0, 4, 0.5);
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .hero-title {
    margin: 0;
    // width: min(92vw, 24ch);

    overflow-wrap: break-word;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(28px, 4vw, 56px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    margin: 0px auto 0px;
    line-height: 1.05;
  }

  .hero-subtitle {
    margin: 0;
    width: min(92vw, 68ch);
    overflow-wrap: break-word;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(15px, 1.4vw, 18px);
    font-family: Red Rose-Light;
    font-weight: 300;
    text-align: center;
    margin: clamp(16px, 4vw, 50px) auto 0;
    line-height: 1.65;
  }

  .hero-cta {
    background-color: rgba(255, 255, 255, 1);
    border-radius: 8px;
    width: fit-content;
    max-width: min(92vw, 420px);
    min-height: var(--tap-min);
    padding: 14px 60px;
    margin: clamp(18px, 4vw, 50px) auto 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .hero-cta-label {
    overflow-wrap: break-word;
    color: rgba(22, 0, 4, 1);
    font-size: 16px;
    font-family: Red Rose-Regular;
    font-weight: 500;
    text-align: left;
    white-space: nowrap;
    line-height: 1;
    text-transform: uppercase;
  }

  .hero-cta-icon {
    width: 18px;
    height: 18px;
  }

  .hero-content {
    width: min(92vw, var(--content-max));
    padding: 0 var(--container-pad);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  // ---------------------------------------------------------------------------
  // Features — why Sakura Floors
  // ---------------------------------------------------------------------------
  .features-section {
    width: 100%;

    &[data-motion='section'] {
      --motion-delay: 0ms;
      --motion-duration: 520ms;
      --motion-distance: 56px;
      --motion-tilt-from: 2.8deg;
    }
  }

  .features-heading {
    overflow-wrap: break-word;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(40px, 6vw, 80px);
    font-family: Red Rose-Regular;
    text-transform: uppercase;
    font-weight: normal;
    text-align: center;
    white-space: normal;
    line-height: 1;
    max-width: var(--content-max);
    margin: clamp(64px, 10vw, 140px) auto 0;
    padding: 0 var(--container-pad);
    box-sizing: border-box;

    &[data-motion='title'] {
      --motion-delay: 0ms;
      --motion-duration: 560ms;
      --motion-distance: 64px;
      --motion-tilt-from: 3.2deg;
    }
  }

  .features-grid {
    width: 100%;
    max-width: var(--content-max);
    padding: 0 var(--container-pad);
    margin: clamp(40px, 6vw, 100px) auto clamp(56px, 8vw, 120px);
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 1fr;
    --features-gap: clamp(28px, 4vw, 40px);
    gap: var(--features-gap);
    align-items: start;

    > * {
      min-width: 0;
    }

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: 640px) and (max-width: 1023px) {
      // When the last row has a single item (odd count), center it.
      > :last-child:nth-child(odd) {
        grid-column: 1 / -1;
        justify-self: center;
        width: min(100%, calc((100% - var(--features-gap)) / 2));
      }
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .feature-card[data-motion='card'] {
      /* Let global motion drive a visible from-bottom entrance */
      --motion-distance: 182px;
      --motion-tilt-from: 8deg;
    }

    .feature-card[data-motion='card'].is-motion-ready {
      /* entrance: from bottom to top (more obvious) */
      transform:
        perspective(1200px)
        translate3d(0, var(--motion-distance), 0)
        rotateX(var(--motion-tilt-from));
      opacity: 0;
      will-change: transform, opacity;
    }

    .feature-card[data-motion='card'].is-motion-visible {
      transform: perspective(1200px) translate3d(0, 0, 0) rotateX(0deg);
      opacity: 1;
      transition:
        opacity 780ms var(--motion-ease-luxury) var(--motion-delay),
        transform 780ms var(--motion-ease-luxury) var(--motion-delay);
    }

    .feature-card[data-motion='card']:nth-child(1) {
      --motion-delay: 0ms;
    }

    .feature-card[data-motion='card']:nth-child(2) {
      --motion-delay: 80ms;
    }

    .feature-card[data-motion='card']:nth-child(3) {
      --motion-delay: 160ms;
    }

  }

  .feature-card {
    width: 100%;
    background-color: rgba(255, 255, 255, 1);
    padding-bottom: 50px;

    // & + .feature-card {
    //   margin-left: 30px;
    // }

    .feature-image {
      width: 100%;
      display: block;
    }

    .feature-title {
      margin: 0;
      max-width: 100%;
      min-height: 40px;
      overflow-wrap: break-word;
      color: rgba(22, 0, 4, 1);
      font-size: 32px;
      font-family: Red Rose-Medium;
      font-weight: 500;
      text-align: left;
      line-height: 32px;
      margin-top: 24px;
    }

    .feature-desc {
      margin: 0;
      overflow-wrap: break-word;
      color: rgba(124, 124, 124, 1);
      font-size: 16px;
      font-family: PingFang SC-Regular;
      font-weight: normal;
      text-align: left;
      line-height: 24px;
      margin: 27px 0 19px;
    }
  }

  // ---------------------------------------------------------------------------
  // USP banner
  // ---------------------------------------------------------------------------
  .usp-banner {
    position: relative;
    overflow: hidden;
    min-height: clamp(600px, 76vh, 900px);
    background: url(../../assets/images/pages-home-usp-banner.png) no-repeat;
    background-size: cover;
    background-position: center;
    width: 100%;
    display: flex;
    flex-direction: column;

    .usp-video {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      z-index: 0;
    }

    .usp-overlay {
      background-color: rgba(22, 0, 4, 0.35);
      position: absolute;
      inset: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }

    .usp-content {
      width: 100%;
      max-width: min(92vw, var(--content-max));
      margin: 0 auto;
      padding: clamp(40px, 6vh, 80px) var(--container-pad);
      box-sizing: border-box;

      @media (min-width: 640px) {
        transform: translateY(25%);
      }
    }

    .usp-title {
      max-width: 100%;
      overflow-wrap: break-word;
      color: rgba(255, 255, 255, 1);
      font-size: clamp(44px, 7vw, 120px);
      font-family: Red Rose-Regular;
      font-weight: normal;
      text-align: center;
      white-space: normal;
      line-height: 1.02;
      margin: 0 auto;
    }

    .usp-text {
      // overflow-wrap: break-word;
      color: rgba(255, 255, 255, 1);
      font-size: clamp(15px, 1.6vw, 18px);
      font-family: Red Rose-Light;
      font-weight: normal;
      text-align: center;
      line-height: 1.75;
      margin: 18px auto 0;
    }
  }

  // ---------------------------------------------------------------------------
  // New arrivals + about + footer
  // ---------------------------------------------------------------------------
  .new-arrivals-section {
    width: 100%;
  }

  .arrivals-intro {
    width: 100%;
    margin: clamp(56px, 8vw, 120px) 0 0;
    padding: 0;
    align-items: flex-start;
    gap: clamp(20px, 3.5vw, 56px);

    .arrivals-title {
      flex: 0 1 40%;
      min-width: 0;
      text-transform: uppercase;
      color: rgba(22, 0, 4, 1);
      font-size: clamp(40px, 4.5vw, 96px);
      font-family: Red Rose-Regular;
      text-align: left;
      line-height: 1;
    }

    .arrivals-desc {
      flex: 1 1 58%;
      min-width: 0;
      overflow-wrap: break-word;
      color: rgba(124, 124, 124, 1);
      font-size: clamp(16px, 1.25vw, 22px);
      font-family: Red Rose-Regular;
      font-weight: normal;
      text-align: left;
      line-height: 1.6;
      margin-top: 0;
    }
  }

  .product-grid {
    width: 100%;
    max-width: 1920px;
    margin: clamp(36px, 5vw, 96px) auto 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
  }

  .product-card {
    position: relative;
    aspect-ratio: 1 / 1.382;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    width: 100%;
    border-radius: 0;
    overflow: hidden;
    padding: clamp(30px, 2.5vw, 35px) clamp(30px, 2.5vw, 40px) clamp(40px, 3.5vw, 60px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;
    isolation: isolate;

    /* Unify card entrance motion (bottom -> top) */
    &[data-motion='card'] {
      --motion-distance: 182px;
      --motion-tilt-from: 8deg;
    }

    &[data-motion='card'].is-motion-ready {
      transform:
        perspective(1200px)
        translate3d(0, var(--motion-distance), 0)
        rotateX(var(--motion-tilt-from));
      opacity: 0;
      will-change: transform, opacity;
    }

    &[data-motion='card'].is-motion-visible {
      transform: perspective(1200px) translate3d(0, 0, 0) rotateX(0deg);
      opacity: 1;
      transition:
        opacity 780ms var(--motion-ease-luxury) var(--motion-delay),
        transform 780ms var(--motion-ease-luxury) var(--motion-delay);
    }

    /* Stagger entrance for 8 cards */
    &[data-motion='card']:nth-child(1) {
      --motion-delay: 120ms;
    }
    &[data-motion='card']:nth-child(2) {
      --motion-delay: 280ms;
    }
    &[data-motion='card']:nth-child(3) {
      --motion-delay: 440ms;
    }
    &[data-motion='card']:nth-child(4) {
      --motion-delay: 600ms;
    }
    &[data-motion='card']:nth-child(5) {
      --motion-delay: 760ms;
    }
    &[data-motion='card']:nth-child(6) {
      --motion-delay: 920ms;
    }
    &[data-motion='card']:nth-child(7) {
      --motion-delay: 1080ms;
    }
    &[data-motion='card']:nth-child(8) {
      --motion-delay: 1240ms;
    }

    & > * {
      position: relative;
      z-index: 1;
    }

    &:nth-child(1) {
      background-image: url(../../assets/images/pages-home-product-card-1.png);
    }
    &:nth-child(2) {
      background-image: url(../../assets/images/pages-home-product-card-5.png);
    }
    &:nth-child(3) {
      background-image: url(../../assets/images/pages-home-product-card-7.png);
    }
    &:nth-child(4) {
      background-image: url(../../assets/images/pages-home-product-card-2.png);
    }
    &:nth-child(5) {
      background-image: url(../../assets/images/pages-home-product-card-3.png);
    }
    &:nth-child(6) {
      background-image: url(../../assets/images/pages-home-product-card-4.png);
    }
    &:nth-child(7) {
      background-image: url(../../assets/images/pages-home-product-card-6.png);
    }
    &:nth-child(8) {
      background-image: url(../../assets/images/pages-home-product-card-8.png);
    }
  }

  .product-name {
    margin: 0;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(34px, 3.2vw, 56px);
    font-family: Red Rose-Medium;
    font-weight: 500;
    text-align: left;
    line-height: 1.05;
    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.15);
  }

  .product-cta {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: end;
    // justify-content: center;
    gap: 30px;
    min-height: var(--tap-min);
  }

  .product-link {
    color: rgba(255, 255, 255, 1);
    font-size: 16px;
    font-family: Red Rose-Regular;
    font-weight: 500;
    text-align: right;
    line-height: 1;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .product-link-icon {
    background-color: rgba(255, 255, 255, 1);
    height: 24px;
    width: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
  }

  .product-link-arrow {
    width: 20px;
    height: 17px;
    display: block;
  }

  // ---------------------------------------------------------------------------
  // About
  // ---------------------------------------------------------------------------
  .about-section {
    width: 100%;
    margin: clamp(64px, 10vw, 140px) auto;
    padding-block: 0;
    .about-title {
      text-transform: uppercase;
      margin: 0;
      overflow-wrap: break-word;
      color: rgba(22, 0, 4, 1);
      font-size: clamp(40px, 6vw, 80px);
      font-family: Red Rose-Regular;
      font-weight: normal;
      text-align: center;
      white-space: normal;
      line-height: 1;
    }

    .about-text {
      max-width: min(72ch, 100%);
      overflow-wrap: break-word;
      color: rgba(124, 124, 124, 1);
      font-size: clamp(16px, 1.25vw, 22px);
      font-family: Red Rose-Regular;
      font-weight: normal;
      text-align: center;
      line-height: 1.6;
      margin: clamp(20px, 3vw, 32px) auto 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Breakpoints
  // ---------------------------------------------------------------------------
  @media (max-width: 1023px) {
    .hero-section {
      min-height: clamp(580px, 74vh, 760px);
    }

    .usp-banner {
      min-height: clamp(540px, 70vh, 720px);
    }

    .product-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      padding-inline: var(--container-pad);
      box-sizing: border-box;
    }
  }

  @media (max-width: 767px) {
    .hero-section {
      min-height: clamp(540px, 70vh, 680px);
    }

    .usp-banner {
      min-height: clamp(500px, 66vh, 640px);
    }

    .arrivals-intro {
      flex-direction: column;
      align-items: flex-start;
    }

    .product-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    /* Mobile: reduce stagger so later cards don't feel delayed */
    .product-card[data-motion='card']:nth-child(1) {
      --motion-delay: 70ms;
    }
    .product-card[data-motion='card']:nth-child(2) {
      --motion-delay: 105ms;
    }
    .product-card[data-motion='card']:nth-child(3) {
      --motion-delay: 140ms;
    }
    .product-card[data-motion='card']:nth-child(4) {
      --motion-delay: 175ms;
    }
    .product-card[data-motion='card']:nth-child(5) {
      --motion-delay: 210ms;
    }
    .product-card[data-motion='card']:nth-child(6) {
      --motion-delay: 245ms;
    }
    .product-card[data-motion='card']:nth-child(7) {
      --motion-delay: 280ms;
    }
    .product-card[data-motion='card']:nth-child(8) {
      --motion-delay: 315ms;
    }
  }
}
